import { useEffect, useMemo, useRef, useState } from "react";

// serializer
import VoucherAvailableSerializer from "issara-sdk/types/VoucherAvailableSerializer_apps_BIL";

import formatComma from "react-lib/utils/formatComma";

import { AlertV4, ButtonV4, ModalV4 } from "../common/ui";

import type { InvoiceDiscount, PayState, ReceiptData } from "../common/sequence/BillingV4";
import { payStatusOf } from "./billingModelV4";
import BillingPayLineV4 from "./BillingPayLineV4";
import {
  discountBahtOf,
  emptyPayLine,
  invoiceDiscountsOf,
  nameOptionsOf,
  packageValueOf,
  PAY_KINDS,
  payLineErrorOf,
  paySummaryOf,
  receiptPaymentsOf,
  round2,
  walletTotalOf,
  walletUsesOf,
  type DiscountUnit,
  type PayKind,
  type PayLine,
} from "./billingPayModelV4";

// Types
type ModalBillingPayV4Props = {
  // data
  pay: PayState;
  patient?: {
    hn?: string | null;
    full_name?: string;
    real_name?: string;
    real_name_th?: string;
    real_name_en?: string;
  } | null;
  doctorOptions?: { value: number | string; text: string }[];
  calcError?: unknown;
  submitError?: unknown;
  submitting?: boolean;
  // callback
  onCalc: (invoiceDiscounts: InvoiceDiscount[], vouchers: number[]) => void;
  onCheckVoucher: (
    code: string,
    onDone: (voucher: VoucherAvailableSerializer | null) => void,
  ) => void;
  onCheckCoupon: (code: string, onDone: (value: string | null) => void) => void;
  onCheckAffiliate: (
    code: string,
    onDone: (affiliate: { id: number; name: string } | null) => void,
  ) => void;
  onSubmit: (data: ReceiptData, receiptNo?: string) => void;
  onCloseError: (key: "calc" | "submit") => void;
  onClose: () => void;
};

// Consts
const CALC_DELAY = 400;

/** pop-up การรับชำระเงิน: ส่วนลด คูปอง Wallet ช่องทางชำระ และออกใบเสร็จ */
const ModalBillingPayV4 = (props: ModalBillingPayV4Props) => {
  const [receiptMode, setReceiptMode] = useState<"auto" | "custom">("auto");
  const [receiptNo, setReceiptNo] = useState("");
  const [discount, setDiscount] = useState("");
  const [discountUnit, setDiscountUnit] = useState<DiscountUnit>("บาท");
  const [couponCode, setCouponCode] = useState("");
  const [couponMessage, setCouponMessage] = useState("");
  const [extraVouchers, setExtraVouchers] = useState<VoucherAvailableSerializer[]>([]);
  const [voucherIds, setVoucherIds] = useState<number[]>([]);
  const [walletPick, setWalletPick] = useState("");
  const [walletIds, setWalletIds] = useState<number[]>([]);
  const [walletTyped, setWalletTyped] = useState<Record<number, string | undefined>>({});
  const [cash, setCash] = useState("");
  const [lines, setLines] = useState<PayLine[]>([]);
  const [tried, setTried] = useState(false);
  const [note, setNote] = useState("");
  const [doctor, setDoctor] = useState(props.pay.doctorId ? String(props.pay.doctorId) : "");
  const [nameLang, setNameLang] = useState(
    props.patient?.real_name_en && props.patient.real_name === props.patient.real_name_en
      ? "EN"
      : "TH",
  );
  const [affiliateCode, setAffiliateCode] = useState("");
  const [affiliateMessage, setAffiliateMessage] = useState("");
  const [affiliate, setAffiliate] = useState<{ id: number; name: string } | null>(null);

  const couponRef = useRef<HTMLInputElement>(null);
  const calcStarted = useRef(false);

  const amount = Math.max(0, Number(discount) || 0);
  const discountBase = Number(props.pay.calc?.subtotal_price) || 0;
  const discountBaht = discountBahtOf(discountUnit, amount, discountBase);
  const invoiceDiscounts = invoiceDiscountsOf(discountBaht);
  const payCount = props.pay.items.filter((item) => payStatusOf(item) === "pending").length;
  const packageItems = props.pay.items.filter((item) => payStatusOf(item) === "package");
  const walletOptions = props.pay.wallets.filter((wallet) => !walletIds.includes(wallet.id ?? 0));
  const packageValue = packageValueOf(packageItems);

  // Memo
  const vouchers = useMemo(
    () => [
      ...props.pay.vouchers,
      ...extraVouchers.filter((extra) => !props.pay.vouchers.some((item) => item.id === extra.id)),
    ],
    [props.pay.vouchers, extraVouchers],
  );

  const walletUses = useMemo(
    () =>
      walletUsesOf(
        props.pay.wallets,
        walletIds,
        walletTyped,
        Number(props.pay.calc?.net_payable) || 0,
      ),
    [props.pay.wallets, walletIds, walletTyped, props.pay.calc?.net_payable],
  );

  const walletTotal = useMemo(() => walletTotalOf(walletUses), [walletUses]);

  const summary = useMemo(
    () => paySummaryOf({ items: props.pay.items, calc: props.pay.calc, walletTotal, cash, lines }),
    [props.pay.items, props.pay.calc, walletTotal, cash, lines],
  );

  const disabled =
    !summary.enough ||
    props.pay.items.length === 0 ||
    (summary.due > 0 && summary.others > summary.due) ||
    !!props.submitting;

  // Effect
  useEffect(() => {
    if (!calcStarted.current && discountBaht === 0 && voucherIds.length === 0) {
      return;
    }

    calcStarted.current = true;

    const timer = setTimeout(() => props.onCalc(invoiceDiscounts, voucherIds), CALC_DELAY);

    return () => clearTimeout(timer);
  }, [discountBaht, voucherIds.join(",")]);

  // Handler
  const handleAddCoupon = () => {
    const code = couponCode.trim().toUpperCase();

    if (!code) {
      setCouponMessage("กรอกเลขคูปองก่อน");

      return;
    }

    const known = vouchers.find((voucher) => voucher.code.toUpperCase() === code);

    if (known?.id && voucherIds.includes(known.id)) {
      setCouponMessage("ใช้คูปองนี้แล้ว");

      return;
    }

    props.onCheckVoucher(code, (voucher) => {
      if (!voucher?.id) {
        setCouponMessage(`ไม่พบคูปอง ${code}`);

        return;
      }

      if (voucherIds.includes(voucher.id)) {
        setCouponMessage("ใช้คูปองนี้แล้ว");

        return;
      }

      setExtraVouchers((list) => [...list.filter((item) => item.id !== voucher.id), voucher]);
      setVoucherIds((ids) => [...ids, voucher.id ?? 0]);
      setCouponCode("");
      setCouponMessage(`เพิ่มคูปอง ${voucher.code}`);
    });
  };

  const handleScanCoupon = () => {
    couponRef.current?.focus();
  };

  const handleToggleVoucher = (id: number) => {
    setVoucherIds(
      voucherIds.includes(id) ? voucherIds.filter((x) => x !== id) : [...voucherIds, id],
    );
  };

  const handleAddWallet = () => {
    const id = Number(walletPick);

    if (!id) {
      return;
    }

    setWalletIds([...walletIds, id]);
    setWalletTyped({ ...walletTyped, [id]: undefined });
    setWalletPick("");
  };

  const handleRemoveWallet = (id: number) => {
    const typed = { ...walletTyped };

    delete typed[id];

    setWalletIds(walletIds.filter((x) => x !== id));
    setWalletTyped(typed);
  };

  const handleChangeWallet = (id: number, value: string | undefined) => {
    setWalletTyped({ ...walletTyped, [id]: value });
  };

  const handleFillCash = () => {
    const left = round2(Math.max(0, summary.due - summary.others));

    setCash(left ? String(left) : "");
  };

  const handleAddLine = (kind: PayKind) => {
    const left = round2(Math.max(0, summary.due - summary.total));

    setLines([...lines, emptyPayLine(kind, left)]);
  };

  const handleChangeLine = (index: number, data: Partial<PayLine>) => {
    setLines(lines.map((line, i) => (i === index ? { ...line, ...data } : line)));
  };

  const handleCheckCoupon = (index: number) => {
    const code = lines[index].coupon.trim();

    if (!code || lines[index].couponCheck) {
      return;
    }

    props.onCheckCoupon(code, (value) => {
      setLines((prev) =>
        prev.map((line, i) =>
          i === index
            ? { ...line, couponCheck: value === null ? "bad" : "ok", amount: value ?? line.amount }
            : line,
        ),
      );
    });
  };

  const handleRemoveLine = (index: number) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleClearLines = () => {
    setLines([]);
    setTried(false);
  };

  const handleCheckAffiliate = () => {
    const code = affiliateCode.trim();

    if (!code) {
      setAffiliate(null);
      setAffiliateMessage("กรอกรหัสก่อน");

      return;
    }

    props.onCheckAffiliate(code, (result) => {
      setAffiliate(result);
      setAffiliateMessage(result ? result.name : "ไม่พบรหัส Affiliate นี้");
    });
  };

  const handlePay = () => {
    if (disabled) {
      return;
    }

    if (lines.some((line) => Object.keys(payLineErrorOf(line)).length > 0)) {
      setTried(true);

      return;
    }

    props.onSubmit(
      {
        payments: receiptPaymentsOf(summary, lines),
        patient_name_lang: nameLang,
        price: summary.net.toFixed(2),
        round_off: props.pay.calc?.round_off || "0.00",
        receipt_doctor: Number(doctor) || null,
        invoice_discounts: invoiceDiscounts,
        user_remark: note,
        vouchers: voucherIds.map(String),
        affiliate: affiliate?.id ?? null,
      },
      receiptMode === "custom" && receiptNo.trim() ? receiptNo.trim() : undefined,
    );
  };

  // Render
  const headExtra = (
    <div className="hv4-bill-pay-head">
      <span>
        {props.patient?.full_name || ""} ({props.patient?.hn || "-"})
      </span>
      <span className="hv4-bill-pay-rc">
        เลขที่ใบเสร็จ :
        <span role="group" aria-label="เลขที่ใบเสร็จ" className="hv4-bill-pay-seg">
          <button
            type="button"
            aria-pressed={receiptMode === "auto"}
            className={receiptMode === "auto" ? "hv4-bill-pay-seg--on" : ""}
            onClick={() => setReceiptMode("auto")}
          >
            AUTO
          </button>
          <button
            type="button"
            aria-pressed={receiptMode === "custom"}
            className={receiptMode === "custom" ? "hv4-bill-pay-seg--on" : ""}
            onClick={() => setReceiptMode("custom")}
          >
            กำหนดเอง
          </button>
        </span>
        {receiptMode === "custom" ? (
          <input
            className="hv4-input hv4-bill-pay-w160"
            aria-label="เลขที่ใบเสร็จที่กำหนดเอง"
            value={receiptNo}
            onChange={(e) => setReceiptNo(e.target.value)}
          />
        ) : (
          <b>AUTO</b>
        )}
      </span>
    </div>
  );

  return (
    <ModalV4
      title="การรับชำระเงิน"
      width={1180}
      className="hv4-bill-pay-modal"
      headExtra={headExtra}
      onClose={props.onClose}
    >
      <div className="hv4-bill-pay">
        <AlertV4 tone="danger" error={props.calcError} onClose={() => props.onCloseError("calc")} />
        <AlertV4
          tone="danger"
          error={props.submitError}
          onClose={() => props.onCloseError("submit")}
        />

        <div className="hv4-bill-pay-body">
          <div className="hv4-bill-pay-left">
            <div className="hv4-bill-pay-grid">
              <label>ค่าใช้จ่ายทั้งหมด</label>
              <div className="hv4-bill-pay-money">{formatComma(summary.subtotal)}</div>

              <label htmlFor="hv4-pay-disc" className="hv4-bill-pay-lb-brand">
                ส่วนลดท้ายบิล
              </label>
              <div className="hv4-bill-pay-row">
                <input
                  id="hv4-pay-disc"
                  className="hv4-input hv4-bill-num"
                  placeholder="กรอกส่วนลด"
                  inputMode="decimal"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
                <select
                  className="hv4-input hv4-bill-pay-w96"
                  aria-label="หน่วยส่วนลด"
                  value={discountUnit}
                  onChange={(e) => setDiscountUnit(e.target.value as DiscountUnit)}
                >
                  <option>บาท</option>
                  <option>%</option>
                </select>
              </div>

              <label htmlFor="hv4-pay-cp" className="hv4-bill-pay-lb-brand">
                คูปองส่วนลด
              </label>
              <div className="hv4-bill-pay-col">
                <div className="hv4-bill-pay-row hv4-bill-pay-row--wrap">
                  <input
                    id="hv4-pay-cp"
                    ref={couponRef}
                    className="hv4-input hv4-bill-pay-cp"
                    placeholder="กรอกเลขคูปอง"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddCoupon()}
                  />
                  <ButtonV4 className="hv4-bill-btn-brand" onClick={handleAddCoupon}>
                    เพิ่ม
                  </ButtonV4>
                  <ButtonV4 variant="ghost" onClick={handleScanCoupon}>
                    สแกนคูปอง
                  </ButtonV4>
                </div>
                <div role="status" className="hv4-bill-pay-msg">
                  {couponMessage}
                </div>
                {vouchers
                  .filter((voucher) => voucherIds.includes(voucher.id ?? 0))
                  .map((voucher) => (
                    <div key={voucher.id} className="hv4-bill-pay-chip">
                      <span>
                        {voucher.code} · ลด {formatComma(Number(voucher.discount_amount) || 0)} บาท
                        (− {formatComma(Number(voucher.discount_amount) || 0)})
                      </span>
                      <button
                        type="button"
                        aria-label={`เอาคูปอง ${voucher.code} ออก`}
                        onClick={() => handleToggleVoucher(voucher.id ?? 0)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                <div className="hv4-bill-pay-sub">คูปองในคลังของสมาชิก (เลือกได้หลายรายการ)</div>
                {props.pay.vouchers.map((voucher) => (
                  <label key={voucher.id} className="hv4-bill-pay-check">
                    <input
                      type="checkbox"
                      aria-label={`ใช้คูปอง ${voucher.code}`}
                      checked={voucherIds.includes(voucher.id ?? 0)}
                      onChange={() => handleToggleVoucher(voucher.id ?? 0)}
                    />
                    {voucher.code} · ลด {formatComma(Number(voucher.discount_amount) || 0)} บาท
                  </label>
                ))}
              </div>

              <label>ส่วนลด / เบิกจากสิทธิ</label>
              <div>
                <input
                  disabled
                  aria-label="ส่วนลด / เบิกจากสิทธิ (รวมจากรายการที่เลือก)"
                  className="hv4-bill-pay-cov"
                  value={summary.coverage > 0 ? `− ${formatComma(summary.coverage)}` : "0.00"}
                />
                <div className="hv4-bill-pay-note">
                  {summary.coverage > 0
                    ? "รวมจากคอลัมน์ส่วนลด / เบิกจากสิทธิ ของรายการที่เลือก"
                    : "ไม่ได้ใช้สิทธิ (ชำระเอง)"}
                </div>
              </div>

              <label>ตัดจาก Wallet / มัดจำ</label>
              <div className="hv4-bill-pay-col">
                <div className="hv4-bill-pay-row">
                  <select
                    className="hv4-input"
                    aria-label="เลือก Wallet ที่ต้องการใช้"
                    value={walletPick}
                    onChange={(e) => setWalletPick(e.target.value)}
                  >
                    <option value="">
                      {walletOptions.length
                        ? `— เลือก Wallet (${walletOptions.length} รายการ) —`
                        : "— ใช้ครบทุก Wallet แล้ว —"}
                    </option>
                    {walletOptions.map((wallet) => (
                      <option key={wallet.id} value={wallet.id}>
                        {wallet.description || props.pay.walletTypes[wallet.type]} · คงเหลือ{" "}
                        {formatComma(Number(wallet.balance) || 0)} ฿
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="hv4-bill-pay-wadd"
                    disabled={!walletPick}
                    onClick={handleAddWallet}
                  >
                    ＋ ใช้
                  </button>
                </div>
                {props.pay.wallets.length === 0 && (
                  <div className="hv4-bill-pay-msg">ไม่มี Wallet ที่มียอดคงเหลือ</div>
                )}
                {walletUses.map(({ wallet, use, warn }) => {
                  const id = wallet.id ?? 0;
                  const label = wallet.description || props.pay.walletTypes[wallet.type];

                  return (
                    <div key={id} className="hv4-bill-pay-wallet">
                      <div className="hv4-bill-pay-wallet-head">
                        <span>
                          {label}
                          <br />
                          <b>คงเหลือ {formatComma(Number(wallet.balance) || 0)} ฿</b>
                        </span>
                        <button
                          type="button"
                          title="เอาออก"
                          aria-label={`เอา ${label} ออก`}
                          onClick={() => handleRemoveWallet(id)}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="hv4-bill-pay-wallet-use">
                        <span>ใช้ยอด</span>
                        <input
                          className="hv4-input hv4-bill-num hv4-bill-pay-w140"
                          inputMode="decimal"
                          aria-label={`ยอดที่ใช้จาก ${label}`}
                          value={walletTyped[id] ?? String(use)}
                          onChange={(e) => handleChangeWallet(id, e.target.value)}
                        />
                        <span>฿</span>
                        <button type="button" onClick={() => handleChangeWallet(id, undefined)}>
                          ใช้สูงสุด
                        </button>
                      </div>
                      {!!warn && <div className="hv4-bill-pay-warn">{warn}</div>}
                    </div>
                  );
                })}
              </div>

              {walletTotal > 0 && payCount > 0 && (
                <>
                  <label>หัก Wallet รวม</label>
                  <div className="hv4-bill-pay-money">− {formatComma(walletTotal)}</div>
                </>
              )}

              <label>ต้องชำระ</label>
              <div className="hv4-bill-pay-money hv4-bill-pay-money--due">
                {formatComma(summary.due)}
              </div>

              <label>ยอดคงเหลือต้องชำระ</label>
              <div
                className={`hv4-bill-pay-remain${summary.remain === 0 ? " hv4-bill-pay-remain--done" : ""}`}
              >
                {formatComma(summary.remain)}
              </div>
            </div>

            {packageItems.length > 0 && (
              <div className="hv4-bill-pay-pkg">
                <b>ใช้สิทธิ์แพ็กเกจ {packageItems.length} รายการ</b>
                <br />
                มูลค่าราคาปกติ {formatComma(packageValue)} ฿ · ชำระ 0 ฿ · ตัดสิทธิ์ตอนยืนยัน
                และออกใบสรุปการใช้สิทธิ์ (ไม่ใช่ใบเสร็จ)
              </div>
            )}

            <div>
              <label className="hv4-label" htmlFor="hv4-pay-note">
                หมายเหตุ
              </label>
              <textarea
                id="hv4-pay-note"
                className="hv4-input hv4-bill-pay-textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          {(payCount > 0 || summary.due > 0) && (
            <div className="hv4-bill-pay-right">
              <div className="hv4-bill-pay-title">
                รูปแบบการชำระ{" "}
                <span>
                  · เงินสดกรอกได้เลย · ช่องทางอื่นกด + เพื่อใส่รายละเอียดด้านล่าง
                  ยอดจะรวมขึ้นมาที่นี่
                </span>
              </div>
              <div className="hv4-bill-pay-methods">
                <div className="hv4-bill-pay-mgrid">
                  <label htmlFor="hv4-pay-cash">เงินสด</label>
                  <input
                    id="hv4-pay-cash"
                    className="hv4-input hv4-bill-num"
                    placeholder="กรอกจำนวนเงิน"
                    inputMode="decimal"
                    value={cash}
                    onChange={(e) => setCash(e.target.value)}
                  />
                  <button
                    type="button"
                    className="hv4-bill-pay-plus"
                    aria-label="ใส่ยอดที่เหลือเป็นเงินสด"
                    title="ใส่ยอดที่เหลือเป็นเงินสด"
                    onClick={handleFillCash}
                  >
                    +
                  </button>
                  {PAY_KINDS.map(({ kind, label }) => {
                    const count = lines.filter((line) => line.kind === kind).length;

                    return (
                      <div key={kind} className="hv4-bill-pay-mrow">
                        <label>
                          {label}
                          {count > 0 && (
                            <span className="hv4-bill-pay-count"> ({count} รายการ)</span>
                          )}
                        </label>
                        <div aria-label={label} className="hv4-bill-pay-sum">
                          {formatComma(summary.kindTotals[kind])}
                        </div>
                        <button
                          type="button"
                          className="hv4-bill-pay-plus"
                          aria-label={`เพิ่มรายละเอียด${label}`}
                          title={`เพิ่มรายละเอียด${label}`}
                          onClick={() => handleAddLine(kind)}
                        >
                          +
                        </button>
                      </div>
                    );
                  })}
                  <label className="hv4-bill-pay-strong">รวมยอดชำระทั้งหมด</label>
                  <div className="hv4-bill-pay-money hv4-bill-pay-money--due">
                    {formatComma(summary.total)}
                  </div>
                  <span />
                </div>
                <div className="hv4-bill-pay-change">
                  <label>เงินทอน</label>
                  <div className="hv4-bill-pay-money">{formatComma(summary.change)}</div>
                  <label>ลดเศษสตางค์</label>
                  <div className="hv4-bill-pay-money">
                    {formatComma(Number(props.pay.calc?.round_off) || 0)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {lines.length > 0 && (
          <section aria-label="รายละเอียดการชำระ" className="hv4-bill-pay-detail">
            <div className="hv4-bill-pay-detail-head">
              <b>รายละเอียดการชำระ</b>
              <ButtonV4 variant="ghost" size="sm" onClick={handleClearLines}>
                ล้างรายละเอียดทั้งหมด
              </ButtonV4>
            </div>
            {lines.map((line, index) => (
              <BillingPayLineV4
                key={index}
                line={line}
                tried={tried}
                accounts={props.pay.accounts}
                edcs={props.pay.edcs}
                onChange={(data) => handleChangeLine(index, data)}
                onRemove={() => handleRemoveLine(index)}
                onCheckCoupon={() => handleCheckCoupon(index)}
              />
            ))}
          </section>
        )}

        <div className="hv4-bill-pay-foot">
          <div className="hv4-bill-pay-foot-item">
            <label htmlFor="hv4-pay-doctor">ชื่อผู้ให้บริการบนใบเสร็จ</label>
            <select
              id="hv4-pay-doctor"
              className="hv4-input hv4-bill-pay-w240"
              value={doctor}
              onChange={(e) => setDoctor(e.target.value)}
            >
              <option value="">-</option>
              {(props.doctorOptions ?? []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>
          <div className="hv4-bill-pay-foot-item hv4-bill-pay-foot-end">
            <label htmlFor="hv4-pay-name">ชื่อผู้รับบริการบนใบเสร็จ</label>
            <select
              id="hv4-pay-name"
              className="hv4-input hv4-bill-pay-w240"
              value={nameLang}
              onChange={(e) => setNameLang(e.target.value)}
            >
              {nameOptionsOf(props.patient).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
            <ButtonV4 className="hv4-bill-pay-submit" disabled={disabled} onClick={handlePay}>
              {packageItems.length > 0 && payCount === 0
                ? "ยืนยันตัดสิทธิ์ และออกใบสรุปการใช้สิทธิ์"
                : packageItems.length > 0
                  ? "รับชำระเงิน ออกใบเสร็จ และตัดสิทธิ์"
                  : "รับชำระเงิน และออกใบเสร็จ"}
            </ButtonV4>
          </div>
        </div>

        <div className="hv4-bill-pay-aff">
          <label htmlFor="hv4-pay-aff">Affiliate code</label>
          <input
            id="hv4-pay-aff"
            className="hv4-input hv4-bill-pay-w200"
            value={affiliateCode}
            onChange={(e) => setAffiliateCode(e.target.value)}
          />
          <ButtonV4 className="hv4-bill-pay-aff-btn" onClick={handleCheckAffiliate}>
            check
          </ButtonV4>
          <span className="hv4-bill-pay-msg">{affiliateMessage}</span>
        </div>
      </div>
    </ModalV4>
  );
};

ModalBillingPayV4.displayName = "ModalBillingPayV4";

export default ModalBillingPayV4;
